import { apiClient } from './client';
import {
  Piece,
  OCRResult,
} from '../../types/api';

export class PiecesService {
  /**
   * Récupérer les pièces jointes d'un courrier
   */
  async getPieces(courrierId: number): Promise<Piece[]> {
    const data = await apiClient.get<any[]>(`/courriers/${courrierId}/pieces`);
    const mapped = (data || []).map((p: any) => ({
      id: p.id,
      courrier_id: courrierId,
      filename: p.filename,
      original_name: p.originalName ?? p.original_name ?? p.filename,
      s3_key: p.s3Key ?? p.s3_key ?? p.filename,
      mime: p.mime,
      size: p.size,
      file_hash: '',
      ocr_text: p.ocrText ?? p.ocr_text,
      ocr_confidence: p.ocrConfidence ?? p.ocr_confidence,
      ocr_status: p.ocrStatus ?? p.ocr_status ?? 'pending',
      ocr_error: p.ocr_error,
      ocr_completed_at: p.ocr_completed_at,
      is_primary: p.is_primary ?? false,
      page_count: p.pages ?? p.page_count,
      processing_metadata: p.processing_metadata,
      created_at: p.createdAt ?? p.created_at ?? new Date().toISOString(),
      updated_at: p.updatedAt ?? p.updated_at,
      url: p.url,
    })) as unknown as Piece[];
    return mapped;
  }

 /**
  * Uploader une pièce jointe liée à un courrier (multipart)
  */
 async uploadPiece(courrierId: number, file: File): Promise<Piece> {
   // Construire le FormData avec uniquement le fichier
   const formData = new FormData();
   formData.append('file', file);

   // Logs pour débogage
   console.log('📎 [PIECES SERVICE] FormData créé pour upload vers /courriers/${courrierId}/pieces:');
   for (const [key, value] of formData.entries()) {
     console.log(`  - ${key}:`, value instanceof File ? `File: ${value.name}, size: ${value.size}` : value);
   }

   const resp = await apiClient.post<any>(`/courriers/${courrierId}/pieces`, formData, {
     headers: { 'Content-Type': 'multipart/form-data' },
   });

   const piece = {
     id: resp.id,
     courrier_id: courrierId,
     filename: resp.filename,
     original_name: resp.originalName ?? resp.filename,
     s3_key: resp.s3Key ?? resp.filename,
     mime: resp.mimeType ?? resp.mime ?? 'application/octet-stream',
     size: resp.size ?? 0,
     file_hash: '',
     ocr_text: '',
     ocr_confidence: 0,
     ocr_status: 'pending' as const,
     is_primary: false,
     created_at: new Date().toISOString(),
   } as unknown as Piece;

   return piece;
 }

 /**
  * Télécharger une pièce jointe
  */
 async downloadPiece(pieceId: number, filename?: string): Promise<void> {
   await apiClient.downloadFile(`/pieces/${pieceId}/file`, filename);
 }

  /**
   * Récupérer le texte OCR d'une pièce jointe
   * Normalise la réponse backend { text, confidence, status } vers { text, confidence, ocrStatus }
   */
  async getOCRText(pieceId: number): Promise<OCRResult> {
    const raw = await apiClient.get<any>(`/pieces/${pieceId}/ocr`);
    return {
      text: raw?.text ?? '',
      confidence: raw?.confidence ?? 0,
      ocrStatus: raw?.ocrStatus ?? raw?.status, // ex: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED'
      pages: raw?.pages,
      extracted_metadata: raw?.extracted_metadata,
    } as OCRResult;
  }

  /**
   * Déclencher l'OCR manuellement pour une pièce jointe
   */
  async triggerOCR(pieceId: number): Promise<{ jobId: string }> {
    return await apiClient.post<{ jobId: string }>(`/pieces/${pieceId}/ocr`);
  }

  /**
   * Récupérer le statut d'un job OCR
   */
  async getOCRJobStatus(jobId: string): Promise<{
    id: string;
    status: 'pending' | 'processing' | 'completed' | 'failed';
    progress?: number;
    error?: string;
    result?: OCRResult;
  }> {
    return await apiClient.get(`/ocr/jobs/${jobId}`);
  }

  /**
   * Valider/corriger le texte OCR
   */
  async validateOCR(
    pieceId: number,
    validatedData: {
      text?: string;
      extracted_metadata?: Record<string, any>;
      confidence?: number;
    },
  ): Promise<OCRResult> {
    return await apiClient.post<OCRResult>(`/pieces/${pieceId}/ocr/validate`, validatedData);
  }

  /**
   * Supprimer une pièce jointe
   */
  async deletePiece(pieceId: number): Promise<void> {
    await apiClient.delete(`/pieces/${pieceId}`);
  }

  /**
   * Mettre à jour les métadonnées d'une pièce jointe
   */
  async updatePiece(
    pieceId: number,
    updates: {
      filename?: string;
      is_primary?: boolean;
    },
  ): Promise<Piece> {
    return await apiClient.put<Piece>(`/pieces/${pieceId}`, updates);
  }
  /**
   * Attacher une pièce déjà uploadée en temporaire (uploads/temp) à un courrier
   */
  async attachTempPiece(
    courrierId: number,
    payload: { tempFilename: string; originalName?: string; mimeType?: string; size?: number }
  ): Promise<Piece> {
    const resp = await apiClient.post<any>(`/courriers/${courrierId}/pieces/attach`, payload);
    const piece = {
      id: resp.id,
      courrier_id: courrierId,
      filename: resp.filename,
      original_name: resp.originalName ?? resp.filename,
      s3_key: resp.s3Key ?? resp.filename,
      mime: resp.mime ?? resp.mimeType ?? 'application/octet-stream',
      size: resp.size ?? 0,
      file_hash: '',
      ocr_text: '',
      ocr_confidence: 0,
      ocr_status: 'pending' as const,
      is_primary: false,
      created_at: resp.createdAt ?? new Date().toISOString(),
      url: resp.url,
    } as unknown as Piece;
    return piece;
  }
}
 
// Export singleton instance
export const piecesService = new PiecesService();